import {Inject, Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {catchError, Observable, of, switchMap, throwError} from 'rxjs';
import {AuthUtils} from 'app/core/auth/auth.utils';
import {UserService} from 'app/core/user/user.service';
import {Auth} from 'firebase/auth';
import {AUTH} from '@core/firebase/firebase-auth.token';
import {firebaseAuthApi} from '@core/firebase/firebase-auth.api';
import {User} from '@models/user.interface';
import {v4 as uuidv4} from 'uuid';
import {buildPath} from '@utils/util-builder';
import {AuthUtilsService} from '@core/auth/guards/auth-utils.service';
import {LoginEventService} from '@services/login-event.service';
import {SessionWarningDialogComponent} from 'app/core/auth/session-warning-dialog/session-warning-dialog.component';

// Fraction of the token's total lifetime that must elapse before the "still
// there?" warning is shown (0.8 = warn once 80% of the time has passed,
// leaving the final 20% for the user to respond).
const WARNING_THRESHOLD_RATIO = 0.8;

@Injectable()
export class AuthService {
    private _authenticated: boolean = false;
    private _expirationTimer: ReturnType<typeof setTimeout> | undefined = undefined;
    private _warningTimer: ReturnType<typeof setTimeout> | undefined = undefined;
    private _lastScheduledIat: number | undefined = undefined;

    /**
     * Constructor
     */
    constructor(
        private readonly _httpClient: HttpClient,
        private readonly _authUtilsService: AuthUtilsService,
        private readonly _userService: UserService,
        @Inject(AUTH) private readonly auth: Auth,
        private readonly _loginEventService: LoginEventService,
        private readonly _router: Router,
        private readonly _matDialog: MatDialog
    ) {
        firebaseAuthApi.authState(this.auth).subscribe(() => {
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
        this._scheduleSessionTimers(token);
    }

    /**
     * Schedules the "still there?" warning and the auto sign-out, based
     * purely on the token's own `iat`/`exp` claims — no `Date.now()` is used
     * anywhere in this calculation. `setTimeout` counts real elapsed time
     * from the moment it's created regardless of what the system clock says
     * (it's driven by a monotonic timer, not the wall clock), so scheduling
     * by duration alone (the token's real lifetime, and a fraction of it)
     * makes this immune to a wrong local system clock — a real case we've
     * seen (a machine with its clock ~1h off caused the warning to fire
     * immediately after login).
     *
     * Only re-schedules when the token actually changed (`iat` differs from
     * the last one we scheduled for). This method runs on every token
     * assignment — login, closing the warning dialog, a page reload
     * (signInUsingToken), and every HTTP request via the interceptor's
     * opportunistic refresh — but in the vast majority of those calls
     * Firebase hasn't actually rotated the token, so there's nothing to do.
     */
    private _scheduleSessionTimers(token: string): void {
        const issuedAtDate = AuthUtils.getIssuedAtDate(token);
        const expirationDate = AuthUtils.getExpirationDate(token);
        if (!issuedAtDate || !expirationDate) {
            return;
        }

        const iat = issuedAtDate.getTime();
        if (this._lastScheduledIat === iat) {
            return;
        }
        this._lastScheduledIat = iat;

        clearTimeout(this._warningTimer);
        clearTimeout(this._expirationTimer);

        const totalLifetimeMs = expirationDate.getTime() - iat;
        const msUntilWarning = totalLifetimeMs * WARNING_THRESHOLD_RATIO;
        const msUntilExpiration = totalLifetimeMs;

        this._warningTimer = setTimeout(() => {
            this._showSessionWarning();
        }, msUntilWarning);

        this._expirationTimer = setTimeout(() => {
            this.signOut();
            this._router.navigateByUrl('/sign-in');
        }, msUntilExpiration);
    }

    /**
     * Formats a millisecond duration as "Xm Ys" for debug logging.
     */
    private static _formatDuration(ms: number): string {
        const totalSeconds = Math.round(ms / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}m ${seconds}s`;
    }

    /**
     * Shows the "still there?" warning dialog. Only closes via the "Aceptar"
     * button or the X icon (disableClose blocks backdrop/Escape dismissal).
     * Closing it either way renews the token, which restarts both timers.
     */
    private _showSessionWarning(): void {
        const dialogRef = this._matDialog.open(SessionWarningDialogComponent, {
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(async () => {
            try {
                const user = this.auth.currentUser;
                if (user) {
                    this.accessToken = await user.getIdToken(true);
                }
            } catch (error) {
                console.error('Forced token refresh failed, falling back to cached token.', error);
                this.refreshToken().then((user) => {
                    if (user) {
                        // @ts-ignore
                        this.accessToken = user.toJSON().stsTokenManager.accessToken;
                    }
                });
            }
        });
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    /**
     * Storage user
     *
     * @param userInfo
     */
    set userData(userInfo: User) {
        localStorage.setItem('user', JSON.stringify(userInfo));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Promise<any> {
        return firebaseAuthApi.sendPasswordResetEmail(this.auth, email);
        //return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPasswordInit(password: string): Observable<any> {
        //this.afAuth.
        return this._httpClient.put<any>(buildPath(['data-core/Account/passUpdate']), password);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        //this.afAuth.
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    async signIn(credentials: { email: string; password: string }): Promise<any> {
        // Throw error if the user is already logged in
        if (this._authenticated) return throwError('User is already logged in.');

        try {
            const result = await firebaseAuthApi.signInWithEmailAndPassword(this.auth, credentials.email, credentials.password);

            // Store the access token in the local storage
            // @ts-ignore
            this.accessToken = result.user.toJSON().stsTokenManager.accessToken;
            this._registerLoginEvent();
            const user: User = await this._authUtilsService.fetchUserFromServer(result.user);

            // Store user in user service
            this._userService.user = user;
            // Store user in local storage
            this.userData = user;
            this._authenticated = true;

            return user;
        } catch (e) {
            this.signOut();
            throw e;
        }
    }

    private _registerLoginEvent(): void {
        this._loginEventService.registerLoginEvent().subscribe({
            error: (error) => {
                console.warn('Login event registration failed.', error);
            }
        });
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Renew token
        return this._httpClient.post('api/auth/refresh-access-token', {
            accessToken: this.accessToken
        }).pipe(
            catchError(() =>
                // Return false
                of(false)
            ),
            switchMap((response: any) => {
                // Store the access token in the local storage
                this.accessToken = response.accessToken;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return true
                return of(true);
            })
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        clearTimeout(this._expirationTimer);
        clearTimeout(this._warningTimer);
        this._lastScheduledIat = undefined;
        // Close any dialogs left open (e.g. a form modal) so they don't overlap
        // the sign-in screen after the redirect.
        this._matDialog.closeAll();
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');
        localStorage.removeItem('companyIdMaster');
        localStorage.removeItem('hotelRFC');
        localStorage.removeItem('user');
        localStorage.removeItem('polizasMenuEnabled');
        localStorage.removeItem('polizasSemiMasterEnabled');
        localStorage.removeItem('siroc_bienvenida_seen');
        // Set the authenticated flag to false
        this._authenticated = false;
        firebaseAuthApi.signOut(this.auth).then(() => {
        });
        // Return the observable
        return of(true);
    }

    /**
     * SingUp User Firebase-SISE
     *
     * admin-unaluna@gm.mx
     * fd5e2e9d
     *
     * contador-unaluna@gm.mx
     * 30a45100
     *
     * sandra-unaluna-p22@gm.mx
     * 093bc912
     *
     * juan@taxsat.com
     * c5d5d41a
     *
     * @param user
     */
    async signUp(user: { email: string; password: string }): Promise<any> {
        user.password = uuidv4().split('-')[0];
        try {
            /*this.refreshToken().then((user) => {
                    this._authService.accessToken = user._delegate.accessToken;
            });*/
            localStorage.setItem('refresh', 'ok');
            const data = await firebaseAuthApi.createUserWithEmailAndPassword(this.auth, user.email, user.password);
            // @ts-ignore
            const token = data.user.toJSON().stsTokenManager.accessToken;
            return {token: token, password: user.password};
        } catch (e) {
            throw (e);
        }
    }

    /**
     * RefreshToken
     */
    async refreshToken(): Promise<any> {
        return this.auth.currentUser;
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        try {
            // Check if the user is logged in
            if (this._authenticated) {
                return of(true);
            }

            // Check the access token availability
            if (!this.accessToken) {
                return of(false);
            }

            // Check the access token expire date
            if (AuthUtils.isTokenExpired(this.accessToken, 1000)) {
                return of(false);
            }

            // If the access token exists and it didn't expire, sign in using it
            return this.signInUsingToken();
        } catch (e) {
            console.error('Error checking authentication status:', e);
            this.signOut();
        }
    }
}
